import { find, sortBy } from 'lodash';
import { OrderedSet } from 'immutable';
import { createSelector } from 'reselect';
import core, { View } from '@gin-betting/core';
import { getMainSpecifiersBySportId } from '@gin-betting/constants';

import { selectIsSportEventViewable, selectRouteBySportEvent } from 'selectors';
import { ParentPath } from 'types';
import { ACTIVE_MARKET_STATUSES } from 'app-constants';
import { getPromoTypeIdsBySport } from 'utils';
import useMemoSelector from './useMemoSelector';

interface Input {
    matchId: string;
    isPromoMarket?: boolean;
    marketIds?: OrderedSet<string>;
}

export const matchPropsToPick = [
    'id' as const,
    'marketsCount' as const,
    'sportId' as const,
    'tournament' as const,
    'startTime' as const,
    'streams' as const,
    'status' as const,
    'liveCoverage' as const,
    'slug' as const,
    'meta' as const,
    'type' as const,
    'markets' as const,
];

export const marketPropsToPick = [
    'id' as const,
    'name' as const,
    'odds' as const,
    'status' as const,
];
export const tournamentPropsToPick = [
    'id' as const,
    'name' as const,
    'slug' as const,
];

type Market =
    | Pick<View.Market, typeof marketPropsToPick[number]>
    | null
    | undefined;

interface Output {
    match: Pick<View.Match, typeof matchPropsToPick[number]> | null;
    market: Market;
    tournament: Pick<
        View.Tournament,
        typeof tournamentPropsToPick[number]
    > | null;
    competitors: View.CompetitorsValid;
    route: ParentPath;
    hasValidStream: boolean;
    hasValidBetradarId: boolean;
}

const {
    entities: {
        selectors: {
            selectMatchCompetitorsValid,
            makeMatchSelector,
            selectMarkets,
            makeMarketSelector,
            makeTournamentSelector,
        },
    },
    hooks: { useSubscription },
    utils: { splitMarketPath },
} = core;

function usePromoItemContainer({
    matchId,
    isPromoMarket,
    marketIds,
}: Input): Output {
    const result = useMemoSelector(
        makePropsSelector(),
        { matchId, isPromoMarket },
        [matchId, isPromoMarket]
    );

    const { market } = result;

    const validMarketIds = getMarketIdsToSubscribe(
        marketIds || OrderedSet<string>(),
        market
    );

    useSubscription({
        variables: {
            sportEventId: matchId,
            marketIds: validMarketIds,
            isTopMarkets: false,
            withMarketsCount: false,
        },
        config: {
            skip: validMarketIds.length === 0,
            batch: false,
            origin: 'hook-usePromoItemContainer',
            method: 'onUpdateSportEvent',
        },
    });

    return result;
}

const makePropsSelector = () => {
    const selectMatch = makeMatchSelector({
        propsToPick: matchPropsToPick,
    });
    const selectMarket = makeMarketSelector({
        propsToPick: marketPropsToPick,
    });
    const selectTournament = makeTournamentSelector({
        propsToPick: tournamentPropsToPick,
        selectTournamentId: createSelector(selectMatch, match =>
            match ? match.tournament : null
        ),
    });

    const selectMatchMarkets = createSelector(
        selectMatch,
        selectMarkets,
        (match, markets): View.Market[] => {
            if (!match) return [];

            return match.markets
                .map(marketId => markets.get(marketId))
                .filter((m): m is View.Market => !!m);
        }
    );

    return createSelector(
        selectMatch,
        selectMatchMarkets,
        selectMarket,
        selectTournament,
        selectMatchCompetitorsValid,
        selectIsSportEventViewable,
        selectRouteBySportEvent,
        (_, props): boolean | undefined => props.isPromoMarket,
        (
            match,
            markets,
            market,
            tournament,
            competitors,
            { hasValidStream, hasValidBetradarId },
            route,
            isPromoMarket
        ) => {
            return {
                match,
                market: isPromoMarket
                    ? findPromoMarketBySport(markets, match?.sportId) || market
                    : market,
                tournament,
                competitors,
                hasValidStream,
                hasValidBetradarId,
                route,
            };
        }
    );
};

const getMainSpecifierValue = (
    market: View.Market,
    mainSpecifiers: string[]
): number | null => {
    const marketMainSpecifier = find(market.specifiers, specifier =>
        mainSpecifiers.includes(specifier.name)
    );

    if (marketMainSpecifier?.value) {
        return +marketMainSpecifier.value;
    }

    return null;
};

const findPromoMarketBySport = (
    markets: View.Market[],
    sportId: string | undefined
): View.Market | undefined => {
    const typeIds = getPromoTypeIdsBySport(sportId);

    if (!typeIds || !sportId) return;

    let promoMarket: View.Market | undefined;

    typeIds.some(typeId => {
        const marketsWithTypeId = markets.filter(
            m =>
                m.typeId === typeId && ACTIVE_MARKET_STATUSES.includes(m.status)
        );

        if (marketsWithTypeId.length === 0) return false;

        if (marketsWithTypeId.length === 1) {
            [promoMarket] = marketsWithTypeId;

            return true;
        }

        const mainSpecifiers = getMainSpecifiersBySportId(sportId);

        [promoMarket] = sortBy(marketsWithTypeId, market =>
            getMainSpecifierValue(market, mainSpecifiers)
        );

        return true;
    });

    return promoMarket;
};

function getMarketIdsToSubscribe(
    marketIds: OrderedSet<string>,
    market: Pick<View.Market, 'id'> | undefined | null
): string[] {
    return marketIds
        .add(market?.id || '')
        .map(id => splitMarketPath(id)?.marketId)
        .filter((v): v is string => !!v)
        .toArray();
}

export default usePromoItemContainer;
